import type { ReactNode } from 'react';
import { useContext } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';

import { Checkbox } from '@/components/ui/checkbox';
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { AgentAuthorityContext } from '@/lib/layout/AgentAuthorityContext';

function CustomCheckbox<
  T extends FieldValues,
  TTransformedValues extends FieldValues | undefined = T,
>({
  control,
  name,
  label,
  disabled = false,
  direction = 'row',
  required = false,
  readOnly = false,
}: {
  control: Control<T, unknown, TTransformedValues>;
  name: Path<T> | string;
  label?: ReactNode;
  disabled?: boolean;
  direction?: 'row' | 'reverse';
  required?: boolean;
  readOnly?: boolean;
}) {
  const { editMode } = useContext(AgentAuthorityContext);
  return (
    <FormField
      control={control}
      name={name as Path<T>}
      render={({ field }) => (
        <FormItem
          className={`flex items-center ${direction === 'reverse' ? 'flex-row-reverse' : ''}`}
        >
          <FormLabel
            htmlFor={String(name)}
            className="ml-1.5 mr-3 leading-[1.5rem]"
            style={{ fontWeight: 'inherit' }}
          >
            {label}
            {required && !disabled ? (
              <span className="text-red-400">*</span>
            ) : null}
          </FormLabel>
          <div className="relative !m-0 flex items-center">
            <Checkbox
              className={`!mt-0 ${!editMode || readOnly ? '!opacity-100 data-[state=checked]:border-brand-600 data-[state=checked]:bg-brand-600' : 'data-[state=checked]:border-brand-green data-[state=checked]:bg-brand-green'}`}
              id={String(name)}
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={disabled || !editMode || readOnly}
            />
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export { CustomCheckbox as Checkbox };
